import type { PostHog } from 'posthog-js'

type Properties = Record<string, unknown>
type MetaCommand = 'init' | 'track' | 'trackCustom' | 'set' | 'consent'

interface MetaPixel {
  (command: MetaCommand, ...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  push: MetaPixel
  loaded: boolean
  version: string
  disablePushState?: boolean
}

declare global {
  interface Window {
    fbq?: MetaPixel
    _fbq?: MetaPixel
  }
}

const posthogKey = import.meta.env.VITE_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_POSTHOG_HOST || '/ingest'
const posthogUiHost = import.meta.env.VITE_POSTHOG_UI_HOST || 'https://us.posthog.com'
const metaPixelId = import.meta.env.VITE_META_PIXEL_ID

export const isPosthogConfigured = Boolean(posthogKey)
export const isMetaPixelConfigured = Boolean(metaPixelId)

let posthog: Promise<PostHog | null> | null = null

const authTokenParams = ['access_token', 'refresh_token', 'id_token', 'provider_token', 'provider_refresh_token', 'token_hash']

function hasAuthCallbackTokens(): boolean {
  const query = new URLSearchParams(window.location.search)
  const fragment = new URLSearchParams(window.location.hash.slice(1))
  return authTokenParams.some((key) => query.has(key) || fragment.has(key))
}

let authCallbackPending = hasAuthCallbackTokens()
const pendingEvents: Array<() => void> = []

export function isAuthTokenCallback(): boolean {
  return authCallbackPending || hasAuthCallbackTokens()
}

export function clearAuthCallbackTokens(): void {
  const url = new URL(window.location.href)
  for (const key of authTokenParams) url.searchParams.delete(key)
  const fragment = new URLSearchParams(url.hash.slice(1))
  if (authTokenParams.some((key) => fragment.has(key))) url.hash = ''
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  authCallbackPending = false
  for (const send of pendingEvents.splice(0)) send()
}

function loadPosthog(): Promise<PostHog | null> {
  if (!posthogKey || isAuthTokenCallback()) return Promise.resolve(null)
  posthog ??= import('posthog-js')
    .then(({ default: client }) => {
      client.init(posthogKey, {
        api_host: posthogHost,
        ui_host: posthogUiHost,
        capture_pageview: false,
        capture_pageleave: true,
        person_profiles: 'identified_only',
      })
      return client
    })
    .catch(() => null)
  return posthog
}

function loadMetaPixel(): MetaPixel | null {
  if (!metaPixelId || typeof window === 'undefined') return null
  if (isAuthTokenCallback()) return null
  if (window.fbq) return window.fbq
  const pixel = function (this: unknown, ...args: unknown[]) {
    if (pixel.callMethod) pixel.callMethod(...args)
    else pixel.queue.push(args)
  } as MetaPixel
  pixel.push = pixel
  pixel.loaded = true
  pixel.version = '2.0'
  pixel.queue = []
  pixel.disablePushState = true
  window.fbq = pixel
  window._fbq = pixel
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
  pixel('init', metaPixelId)
  return pixel
}

export function initAnalytics(): void {
  void loadPosthog()
  loadMetaPixel()
}

export function capture(event: string, properties: Properties = {}): void {
  if (authCallbackPending) { pendingEvents.push(() => capture(event, properties)); return }
  void loadPosthog().then((client) => client?.capture(event, properties))
}

export function trackPageView(path: string): void {
  const url = new URL(path, window.location.origin)
  for (const key of authTokenParams) url.searchParams.delete(key)
  if (authCallbackPending) { pendingEvents.push(() => trackPageView(url.pathname + url.search)); return }
  capture('$pageview', { $current_url: window.location.origin + url.pathname + url.search })
  loadMetaPixel()?.('track', 'PageView')
}

export function trackMeta(event: string, eventId: string, properties: Properties = {}): void {
  if (authCallbackPending) { pendingEvents.push(() => trackMeta(event, eventId, properties)); return }
  loadMetaPixel()?.('track', event, properties, { eventID: eventId })
}

export function identify(userId: string, properties: Properties = {}): void {
  if (authCallbackPending) { pendingEvents.push(() => identify(userId, properties)); return }
  void loadPosthog().then((client) => {
    if (client && client.get_distinct_id() !== userId) client.identify(userId, properties)
  })
}

export function resetAnalytics(): void {
  if (authCallbackPending) { pendingEvents.push(resetAnalytics); return }
  void loadPosthog().then((client) => client?.reset())
}
